import React, { useEffect } from 'react';

import { useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';

import FormLayout from 'components/Layouts/Form';
import GuestPortalFlights from 'components/Pages/Admin/GuestPortal/Flights';
import GuestPortalGeneralInfo from 'components/Pages/Admin/GuestPortal/GeneralInfo';
import GuestPortalGuestForms from 'components/Pages/Admin/GuestPortal/GuestForms';
import GuestPortalLanding from 'components/Pages/Admin/GuestPortal/Landing';
import GuestPortalTravelWallet from 'components/Pages/Admin/GuestPortal/TravelWallet';
import { Costs } from 'components/Pages/Admin/Itinerary/Costs';
import ItineraryDetails from 'components/Pages/Admin/Itinerary/Details';
import ItineraryGeneralInfo from 'components/Pages/Admin/Itinerary/GeneralInfo';
import ItineraryLanding from 'components/Pages/Admin/Itinerary/Landing';
import LookbookGeneralInfo from 'components/Pages/Admin/Lookbook/GeneralInfo';
import LookbookLanding from 'components/Pages/Admin/Lookbook/Landing';
import Backdrop from 'components/shared/Backdrop';
import ErrorScreen from 'components/shared/ErrorScreen';
import Loader from 'components/shared/Loader';
import { ITINERARIES_ITEM_TYPES, LIBRARY_CONTENT } from 'constants/index';
import useUnsavedChangesHistoryBlocker from 'hooks/pages/useUnsavedChangesHistoryBlocker';
import useTheme from 'hooks/useTheme';
import i18n, { loadTranslations } from 'i18n';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { accountIdSelector, isAgentSelector, isSuperAdminSelector } from 'store/user/selectors';
import { ItinerariesItemProvider } from 'utils/itineraries/context';
import { mapPathSegment2ItemType } from 'utils/itineraries/functions';
import { useItinerariesItemStepPath } from 'utils/itineraries/functions/useItinerariesItemStepPath';
import { GUEST_PORTAL_STEPS } from 'utils/itineraries/guestPortal/map';
import { prepareItineraryWithinPDF } from 'utils/itineraries/guestPortal/prepareGuestPortal';
import { ITINERARY_STEPS } from 'utils/itineraries/itinerary/map';
import { LOOKBOOK_STEPS } from 'utils/itineraries/lookbook/map';
import { getProposalSteps, ITINERARIES_ITEM_TYPE_TO_STEP_PATH_MAP } from 'utils/itineraries/map';
import logger from 'utils/logger';
import { mapLibType2Theme } from 'utils/map';

import PDFPreviewDataWrapper from '../../../Pages/Admin/shared/PDFPreviewDataWrapper';
import { useItinerariesItemLoader } from './useLoader';

const LOOKBOOK_STEP_TO_COMPONENT_MAP = {
  [LOOKBOOK_STEPS.GENERAL_INFO]: LookbookGeneralInfo,
  [LOOKBOOK_STEPS.LANDING]: LookbookLanding,
};
const ITINERARY_STEP_TO_COMPONENT_MAP = {
  [ITINERARY_STEPS.DETAILS]: ItineraryDetails,
  [ITINERARY_STEPS.GENERAL_INFO]: ItineraryGeneralInfo,
  [ITINERARY_STEPS.COSTS]: Costs,
  [ITINERARY_STEPS.LANDING]: ItineraryLanding,
};
const GUEST_PORTAL_STEP_TO_COMPONENT_MAP = {
  [GUEST_PORTAL_STEPS.GENERAL_INFO]: GuestPortalGeneralInfo,
  [GUEST_PORTAL_STEPS.GUEST_FORMS]: GuestPortalGuestForms,
  [GUEST_PORTAL_STEPS.RELOCATIONS]: GuestPortalFlights,
  [GUEST_PORTAL_STEPS.TRAVEL_WALLET]: GuestPortalTravelWallet,
  [GUEST_PORTAL_STEPS.LANDING]: GuestPortalLanding,
};

const ITINERARIES_ITEM_TYPE_TO_COMPONENT_MAP = {
  [ITINERARIES_ITEM_TYPES.ITINERARY]: ITINERARY_STEP_TO_COMPONENT_MAP,
  [ITINERARIES_ITEM_TYPES.LOOKBOOK]: LOOKBOOK_STEP_TO_COMPONENT_MAP,
  [ITINERARIES_ITEM_TYPES.GUEST_PORTAL]: GUEST_PORTAL_STEP_TO_COMPONENT_MAP,
};

export default function ItinerariesItemRoute() {
  const accId = useSelector(accountIdSelector);
  const { itemType: itemTypePathSegment, itemId, libType } = useParams();
  const itemType = mapPathSegment2ItemType(itemTypePathSegment);

  if (!itemType) {
    logger.error(`[ItinerariesItemRoute] unknown item type supplied: '${itemTypePathSegment}'.`);
    return <ErrorScreen status="404" />;
  }

  if (localStorage.getItem('pdfPreview') === 'true') {
    const prepareItemDataFn = itemType === ITINERARIES_ITEM_TYPES.GUEST_PORTAL ? prepareItineraryWithinPDF : undefined;
    const innerItemType =
      itemType === ITINERARIES_ITEM_TYPES.ITINERARY && libType === LIBRARY_CONTENT
        ? ITINERARIES_ITEM_TYPES.SAMPLE
        : itemType;
    return (
      <PDFPreviewDataWrapper
        itemType={innerItemType}
        itemId={itemId}
        accId={accId}
        prepareItemDataFn={prepareItemDataFn}
      />
    );
  }

  return <ItemRouteGuard itemType={itemType} />;
}

function ItemRoute({ itemType }) {
  const { itemId, step: pathStep, libType } = useParams();
  useTheme(mapLibType2Theme(libType));
  useUnsavedChangesHistoryBlocker();
  const { isFetching, isError } = useItinerariesItemLoader(itemType);
  const step = ITINERARIES_ITEM_TYPE_TO_STEP_PATH_MAP[itemType][pathStep];
  const itinerariesItemData = useSelector(itinerariesItemDataSelector);

  useEffect(() => {
    if (pathStep !== 'landing') {
      loadTranslations(i18n);
    } else {
      loadTranslations(i18n, itinerariesItemData?.settings?.language);
    }

    return () => {
      i18n.locale = 'en';
    };
  }, [i18n.version, pathStep]);

  if (isFetching) {
    return (
      <Backdrop isOpen>
        <Loader />
      </Backdrop>
    );
  }

  if (isError) {
    return <ErrorScreen status="404" />;
  }

  // Need this check for a case when itemType has changed but the data is not started loading yet due to its asynchronous nature
  if (itinerariesItemData && itinerariesItemData.type !== itemType) {
    return null;
  }

  const Component = ITINERARIES_ITEM_TYPE_TO_COMPONENT_MAP[itemType][step];

  return (
    <ItinerariesItemProvider itemId={itemId} itemType={itemType} step={step}>
      <FormLayout type="itinerariesItem">
        <Component />
      </FormLayout>
    </ItinerariesItemProvider>
  );
}

function ItemRouteGuard({ itemType }) {
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const isAgent = useSelector(isAgentSelector);
  const hasAccess = isSuperAdmin || isAgent;

  const { itemId, step: pathStep } = useParams();
  const step = ITINERARIES_ITEM_TYPE_TO_STEP_PATH_MAP[itemType][pathStep];
  const isValidStep = Boolean(step);

  const stepList = getProposalSteps(itemType);
  const getItinerariesItemStepPath = useItinerariesItemStepPath(itemType);

  if (!hasAccess) {
    return <ErrorScreen status="403" />;
  }

  if (!isValidStep) {
    logger.error(`[${ItemRoute.name}] unknown step supplied: '${pathStep}'. For item type: '${itemType}'`);
    return <ErrorScreen status="404" />;
  }

  if (itemId === '0') {
    const isFirstStep = stepList.indexOf(step) === 0;
    if (!isFirstStep) {
      return (
        <Redirect
          to={{
            pathname: getItinerariesItemStepPath(stepList[0], 0),
          }}
        />
      );
    }
  }

  return <ItemRoute itemType={itemType} />;
}
